<template>
  <template v-if="viewModel.isVisible">
    <PopupContent :bodyClass="['quiz-popup-body']">
      <template #header>
        <div class="quiz-header">
          <div class="quiz-progress">
            <span>{{ Math.min(viewModel.stepNumber + 1, viewModel.totalWords) }} / {{ viewModel.totalWords }}</span>
          </div>
        </div>
      </template>

      <!-- Quiz Content -->
      <Transition name="slide" mode="out-in">
        <div v-if="!viewModel.isFinished" :key="viewModel.stepNumber" class="quiz-content">
          <div class="word-display">
            <div class="word-label">{{ $t('quiz.translateWord') }}</div>
            <div class="word-text" :class="getTextClass(viewModel.learnedLanguage)">
              {{ viewModel.currentWord?.validVariant.original }}<br>
              <span
                  v-if="viewModel.currentWord?.validVariant.transliteration && viewModel.currentWord?.validVariant.transliteration!=viewModel.currentWord?.validVariant.original"
                  class="main-word-transliteration"
                  :class="getTextClass(viewModel.nativeLanguage)"
              >
                {{ viewModel.currentWord?.validVariant.transliteration }}
              </span>
            </div>
          </div>

          <div class="answer-options">
            <!-- Answer option buttons -->
            <button
                v-for="(option, index) in viewModel.answerOptions"
                :key="option.id ?? `${index}-${option.original}-${option.translation}`"
                :class="[
                'pill-btn', 'answer-button',
                {
                  'selected': viewModel.selectedOption === option && !viewModel.showAnswerHighlight,
                  'highlight-correct': viewModel.showAnswerHighlight && option.translation === viewModel.currentWord?.validVariant.translation,
                  'highlight-incorrect': viewModel.showAnswerHighlight && option.translation === viewModel.selectedAnswer && !viewModel.isAnswerCorrect,
                  'disabled': viewModel.showAnswerHighlight
                }
              ]"
                @click="viewModel.selectOption(option)"
                :disabled="viewModel.showAnswerHighlight"
            >
              {{ viewModel.getDisplayText(option) }}
            </button>
          </div>
        </div>

        <!-- Results Screen -->
        <div v-else key="results" class="results-content">
          <div class="results-title">{{ $t('quiz.results.completeTitle') }}</div>
          <div class="results-stats">
            <div class="stat-item correct">
              <div class="stat-value">{{ viewModel.validAnswerCount }}</div>
              <div class="stat-label">{{ $t('quiz.results.correct') }}</div>
            </div>
            <div class="stat-item incorrect">
              <div class="stat-value">{{ viewModel.invalidAnswerCount }}</div>
              <div class="stat-label">{{ $t('quiz.results.incorrect') }}</div>
            </div>
            <div class="stat-item score">
              <div class="stat-value">{{ viewModel.scorePercentage }}%</div>
              <div class="stat-label">{{ $t('quiz.results.score') }}</div>
            </div>
          </div>

          <div v-if="showWordsList" class="quiz-words-panel">
            <div class="quiz-words-title">
              📚 {{ $t('quiz.results.wordsTitle') }}
            </div>
            <WordCard
              v-for="(quizWord, index) in viewModel.quizWords"
              :key="quizWord.validVariant.id ?? `${index}-${quizWord.validVariant.original}-${quizWord.validVariant.translation}`"
              :word="quizWord.validVariant.original"
              :translation="quizWord.validVariant.translation"
              :transliteration="quizWord.validVariant.transliteration"
              :source-word-class="getTextClass(viewModel.learnedLanguage)"
              :translation-word-class="getTextClass(viewModel.nativeLanguage)"
              :translit-word-class="getTextClass(viewModel.nativeLanguage)"
              :mnemonic-image-url="quizWord.validVariant.id ? getMnemonic(quizWord.validVariant.id)?.imageUrl : undefined"
              :mnemonic-text="quizWord.validVariant.id ? getMnemonic(quizWord.validVariant.id)?.description : undefined"
              :saved="false"
              :saving="false"
              class="quiz-word-card"
            >
              <template #meta-start>
                <span
                  class="quiz-word-result-icon"
                  :class="{ correct: viewModel.getWordResult(index)===true, incorrect: viewModel.getWordResult(index)===false }"
                  :aria-label="viewModel.getWordResult(index)===true ? 'correct':'incorrect'"
                >
                  {{ viewModel.getWordResult(index)===false ? '❌':'✅' }}
                </span>
              </template>
            </WordCard>
          </div>
        </div>
      </Transition>

      <template #footer>
        <div v-if="!viewModel.isFinished" class="recall-status-selector">
          <button
              class="pill-btn status-button status-hard"
              @click="viewModel.submitAnswer(RecallStatus.Hard)"
              :disabled="viewModel.showAnswerHighlight || !viewModel.selectedOption"
              :title="$t('quiz.recall.hardTitle')"
          >
            <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M5 12h14"/></svg>
            <span class="button-label">{{ $t('quiz.recall.maybe') }}</span>
          </button>

          <button
              class="pill-btn status-button status-good"
              @click="viewModel.submitAnswer(RecallStatus.Good)"
              :disabled="viewModel.showAnswerHighlight || !viewModel.selectedOption"
              :title="$t('quiz.recall.goodTitle')"
          >
            <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            <span class="button-label">{{ $t('quiz.recall.sure') }}</span>
          </button>

          <button
              class="pill-btn status-button status-easy"
              @click="viewModel.submitAnswer(RecallStatus.Easy)"
              :disabled="viewModel.showAnswerHighlight || !viewModel.selectedOption"
              :title="$t('quiz.recall.easyTitle')"
          >
            <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L8 16l-4-4"/><path d="M23 6L13 16"/></svg>
            <span class="button-label">{{ $t('quiz.recall.verySure') }}</span>
          </button>

          <button
              class="pill-btn status-button status-skip"
              @click="viewModel.handleDontKnow()"
              :disabled="viewModel.showAnswerHighlight"
              title="Skip"
          >
            <svg class="status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>
            <span class="button-label">skip</span>
          </button>
        </div>

        <div v-else class="results-actions">
          <button class="pill-btn pill-btn--ghost show-words-button" @click="showWordsList = !showWordsList">
            {{ $t('quiz.results.showWords') }}
          </button>
          <button class="pill-btn pill-btn--primary restart-button" @click="handleClose">{{ $t('common.close') }}</button>
        </div>
      </template>
    </PopupContent>
  </template>

  <!-- Feedback Notification - Fixed Position -->
  <Transition name="slide-down">
    <div v-if="viewModel.showFeedbackNotification"
         :class="['feedback-notification', viewModel.lastAnswerCorrect ? 'correct' : 'incorrect']">
      <span class="notification-icon">{{ viewModel.lastAnswerCorrect ? '✓':'✗' }}</span>
      <span class="notification-text">{{
          viewModel.lastAnswerCorrect ? $t('quiz.feedback.correct'):$t('quiz.feedback.wrong')
        }}</span>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RecallStatus, type IQuizPopupViewModel } from '@lingvo-news/contracts'
import PopupContent from '../components/PopupContent.vue';
import WordCard from '../components/WordCard.vue';
import { getTextClass } from '../styles/get-text-classes';
import { useWordMnemonics } from '../../composables/useWordMnemonics';

interface Props {
  viewModel: IQuizPopupViewModel
}

const props = defineProps<Props>()

const viewModel = props.viewModel
const showWordsList = ref(false);
const wordIdsForMnemonics = computed(() =>
    viewModel.quizWords.map((word) => word.validVariant.id).filter((id): id is string => Boolean(id))
);
const { mnemonicsByWordId } = useWordMnemonics(wordIdsForMnemonics);
const getMnemonic = (wordId: string) => mnemonicsByWordId.value.get(wordId);

const handleClose = (): void => {
  viewModel.handleClose();
}

watch(
    () => viewModel.isFinished,
    (isFinished) => {
      if (!isFinished) {
        showWordsList.value = false;
      }
    }
);
</script>

<style scoped lang="less">
@import "../styles/global.less";
@import "../styles/text-styles.less";

.quiz-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

:deep(.quiz-popup-body) {
  overflow-x: hidden !important;
}

.main-word-transliteration {
  display: block;
  margin-top: 8px;
  font-size: var(--font-size-header);
  color: #9a9a9a;
  line-height: 1.1;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 30px;
}

.feedback-notification {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: var(--font-size-button);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 2000;

  &.correct {
    background: #d4edda;
    color: #155724;
    border: 2px solid #28a745;
  }

  &.incorrect {
    background: #f8d7da;
    color: #721c24;
    border: 2px solid #dc3545;
  }
}

.notification-icon {
  font-size: var(--font-size-header);
}

.notification-text {
  font-size: var(--font-size-button);
}

.quiz-progress {
  font-size: var(--font-size-button);
  color: #666;
}


.toggle-transliteration {
  height: 32px;
  min-width: 48px;
  padding: 6px 12px;

  &:active {
    transform: scale(0.95);
  }
}

.quiz-content {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.word-display {
  text-align: center;
  margin-bottom: 32px;
}

.word-label {
  font-size: var(--font-size-menu);
  color: #666;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.word-text {
  font-size: calc(1.55 * var(--font-size-header));
  color: #333;
  line-height: 0.8;
}

.answer-options {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.answer-button {
  font-size: var(--font-size-regular);
  text-align: left;
  justify-content: flex-start;
  border-radius: 12px;
  white-space: normal;

  &:hover:not(.disabled):not(.highlight-correct):not(.highlight-incorrect) {
    border-color: #667eea;
  }

  &:active:not(.disabled) {
    transform: translateY(0);
  }

  &.disabled {
    cursor: not-allowed;
  }

  &.selected {
    background: #eff6ff;
    border-color: #2563eb;
    color: #1d4ed8;
  }

  &.highlight-correct {
    background: #d4edda;
    border-color: #28a745;
    color: #155724;
    animation: pulse-green 0.35s ease;
  }

  &.highlight-incorrect {
    background: #f8d7da;
    border-color: #dc3545;
    color: #721c24;
    animation: shake 0.3s ease;
  }
}

/* Recall Status Selector */
.recall-status-selector {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: stretch;
  padding: 0;
}

.status-button {
  --button-padding-y-scale: 1;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 0;
  min-width: 90px;
  padding: 10px 8px;
  border-radius: 12px;
  white-space: nowrap;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .status-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  .button-label {
    font-size: var(--font-size-small);
    color: #888;
    white-space: nowrap;
    line-height: 1;
  }
}

.status-again {
  border-color: #f0c4c8;
  .status-icon { color: #dc3545; }
  &:hover:not(:disabled) { background: #fdf0f1; border-color: #e8a0a8; }
}

.status-hard {
  border-color: #f0dca0;
  .status-icon { color: #d4a017; }
  &:hover:not(:disabled) { background: #fdf8ec; border-color: #e0c878; }
}

.status-good {
  border-color: #b8e0c0;
  .status-icon { color: #28a745; }
  &:hover:not(:disabled) { background: #f0faf2; border-color: #90d0a0; }
}

.status-easy {
  border-color: #a8d8e8;
  .status-icon { color: #0d8da0; }
  &:hover:not(:disabled) { background: #eef8fb; border-color: #80c8d8; }
}

.status-skip {
  border-color: #ddd;
  .status-icon { color: #999; }
  &:hover:not(:disabled) { background: #f5f5f5; border-color: #ccc; }
}

/* Results Screen */
.results-content {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
}

.quiz-words-panel {
  width: 100%;
  margin-top: 8px;
  border-top: 1px solid #e9ecef;
  padding-top: 12px;
  text-align: left;
}

.quiz-words-title {
  font-size: var(--font-size-regular);
  color: #333;
  margin-bottom: 12px;
}

.quiz-word-card {
  margin-bottom: 10px;

  :deep(.word-bookmark-button) {
    display: none;
  }
}

.quiz-word-result-icon {
  font-size: var(--font-size-subheader);
  line-height: 1;
}

.quiz-word-result-icon.correct {
  filter: saturate(1.08);
}

.quiz-word-result-icon.incorrect {
  filter: saturate(1.08);
}

.results-title {
  font-size: var(--font-size-header);

  color: #333;
  margin-bottom: 32px;
}

.results-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  flex-wrap: wrap;
  justify-content: center;
}

.stat-item {
  padding: 20px 24px;
  border-radius: 12px;
  min-width: 100px;

  &.correct {
    background: #d4edda;
    border: 2px solid #28a745;
  }

  &.incorrect {
    background: #f8d7da;
    border: 2px solid #dc3545;
  }

  &.score {
    background: #d1ecf1;
    border: 2px solid #17a2b8;
  }
}

.stat-value {
  font-size: calc(1.8 * var(--font-size-header));

  line-height: 1;
  margin-bottom: 8px;

  .correct & {
    color: #28a745;
  }

  .incorrect & {
    color: #dc3545;
  }

  .score & {
    color: #17a2b8;
  }
}

.stat-label {
  font-size: var(--font-size-menu);
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.restart-button {
  padding-left: 40px;
  padding-right: 40px;
}

.results-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.show-words-button {
  font-size: var(--font-size-button);
}

/* Animations */
@keyframes pulse-green {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 8px rgba(40, 167, 69, 0);
  }
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active {
  transition: all 0.3s ease;
}

.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-enter-active {
  transition: all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-leave-active {
  transition: all 0.2s cubic-bezier(0.55, 0.09, 0.68, 0.53);
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-100px);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .quiz-header {
    min-height: 0;
  }

  .quiz-progress {
    font-size: var(--font-size-menu);
  }

  .word-label {
    font-size: var(--font-size-menu);
  }

  .word-text {
    font-size: calc(1.45 * var(--font-size-header));
  }

  .answer-button {
    font-size: var(--font-size-button);
  }

  .results-title {
    font-size: var(--font-size-header);
    margin-bottom: 32px;
  }

  .results-stats {
    gap: 16px;
  }

  .stat-item {
    padding: 16px 20px;
    min-width: 90px;
  }

  .stat-value {
    font-size: calc(1.45 * var(--font-size-header));
  }

  .stat-label {
    font-size: var(--font-size-caption);
  }

  .restart-button {
    font-size: var(--font-size-button);
  }

  .quiz-content {
    min-height: 350px;
  }

  .results-content {
    min-height: 350px;
  }

  .recall-status-selector {
    flex-wrap: wrap;
    gap: 6px;
  }

  .status-button {
    flex: 0 0 auto;
    width: auto;
    padding: 8px;

    .status-icon {
      width: 18px;
      height: 18px;
    }
  }
}

@media (max-width: 480px) {
  .word-text {
    font-size: var(--font-size-header);
  }

  .answer-button {
    font-size: var(--font-size-regular);
    padding: 14px 16px;
  }

  .results-title {
    font-size: var(--font-size-subheader);
    margin-bottom: 24px;
  }

  .stat-item {
    padding: 12px 16px;
    min-width: 80px;
  }

  .stat-value {
    font-size: var(--font-size-header);
  }

  .restart-button {
    font-size: var(--font-size-regular);
  }
}
</style>
